import React, { useEffect, useMemo, useState } from 'react';
import { getAllDealerAssistant, getMarketplaceSettings, setMarketplaceSettings } from '../services/api';
import SearchableDropdown from './SearchableDropdown';
import './MarketplaceSettings.css';
import './AgentEditor.css';

const DEFAULT_SETTINGS = {
  assistant_id: '',
  start_hour: 10,
  end_hour: 22,
  task_interval_minutes: 5,
};

const MarketplaceSettings = () => {
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistantId, setSelectedAssistantId] = useState('');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loadingAssistants, setLoadingAssistants] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', isError: false });

  useEffect(() => {
    const fetchAssistants = async () => {
      setLoadingAssistants(true);
      try {
        const response = await getAllDealerAssistant();
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setAssistants(list.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (error) {
        setMessage({ text: 'Failed to load assistants.', isError: true });
      } finally {
        setLoadingAssistants(false);
      }
    };
    fetchAssistants();
  }, []);

  useEffect(() => {
    if (!selectedAssistantId) {
      setSettings(DEFAULT_SETTINGS);
      return;
    }

    const fetchSettings = async () => {
      setLoadingSettings(true);
      setMessage({ text: '', isError: false });
      try {
        const response = await getMarketplaceSettings(selectedAssistantId);
        const data = response.data || {};
        setSettings({
          assistant_id: data.assistant_id || selectedAssistantId,
          start_hour: Number.isInteger(data.start_hour) ? data.start_hour : DEFAULT_SETTINGS.start_hour,
          end_hour: Number.isInteger(data.end_hour) ? data.end_hour : DEFAULT_SETTINGS.end_hour,
          task_interval_minutes: Number.isInteger(data.task_interval_minutes)
            ? data.task_interval_minutes
            : DEFAULT_SETTINGS.task_interval_minutes,
        });
      } catch (error) {
        setSettings({ ...DEFAULT_SETTINGS, assistant_id: selectedAssistantId });
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [selectedAssistantId]);

  const validationError = useMemo(() => {
    if (!selectedAssistantId) return 'Select an assistant first.';
    if (settings.end_hour <= settings.start_hour) return 'End hour must be greater than start hour.';
    if (settings.task_interval_minutes < 1 || settings.task_interval_minutes > 1440) {
      return 'Interval must be between 1 and 1440 minutes.';
    }
    return '';
  }, [selectedAssistantId, settings.end_hour, settings.start_hour, settings.task_interval_minutes]);

  const assistantOptions = useMemo(
    () =>
      assistants.map((assistant) => ({
        key: assistant.name || assistant.assistant_id,
        value: assistant.assistant_id,
        valueDisplay: assistant.assistant_id,
      })),
    [assistants]
  );

  const handleNumberChange = (field, value) => {
    const parsed = Number(value);
    setSettings((prev) => ({ ...prev, [field]: Number.isNaN(parsed) ? '' : parsed }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validationError) {
      setMessage({ text: validationError, isError: true });
      return;
    }

    setSaving(true);
    setMessage({ text: '', isError: false });
    try {
      await setMarketplaceSettings({
        assistant_id: selectedAssistantId,
        start_hour: Number(settings.start_hour),
        end_hour: Number(settings.end_hour),
        task_interval_minutes: Number(settings.task_interval_minutes),
      });
      setMessage({ text: 'Marketplace settings saved.', isError: false });
    } catch (error) {
      setMessage({ text: 'Failed to save marketplace settings.', isError: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="marketplace-settings-container">
      <div className="legacy-banner">
        Legacy: prefer enabling Marketplace on the Agents editor (`products.marketplace`).
        This page still writes the old `marketplace_settings` collection.
      </div>
      <h2>Marketplace Settings (Legacy)</h2>

      <div className="marketplace-settings-card">
        <div className="marketplace-settings-row">
          <label htmlFor="assistant-select">Assistant</label>
          <SearchableDropdown
            options={assistantOptions}
            value={selectedAssistantId}
            onChange={setSelectedAssistantId}
            placeholder={loadingAssistants ? 'Loading assistants...' : 'Search assistant...'}
          />
        </div>

        <form onSubmit={handleSubmit} className="marketplace-settings-form">
          <div className="marketplace-settings-grid">
            <div>
              <label htmlFor="start-hour">Start Hour</label>
              <input
                id="start-hour"
                type="number"
                min="0"
                max="23"
                value={settings.start_hour}
                onChange={(e) => handleNumberChange('start_hour', e.target.value)}
                disabled={!selectedAssistantId || loadingSettings || saving}
              />
            </div>

            <div>
              <label htmlFor="end-hour">End Hour</label>
              <input
                id="end-hour"
                type="number"
                min="0"
                max="23"
                value={settings.end_hour}
                onChange={(e) => handleNumberChange('end_hour', e.target.value)}
                disabled={!selectedAssistantId || loadingSettings || saving}
              />
            </div>

            <div>
              <label htmlFor="interval">Task Interval (minutes)</label>
              <input
                id="interval"
                type="number"
                min="1"
                max="1440"
                value={settings.task_interval_minutes}
                onChange={(e) => handleNumberChange('task_interval_minutes', e.target.value)}
                disabled={!selectedAssistantId || loadingSettings || saving}
              />
            </div>
          </div>

          <button type="submit" disabled={Boolean(validationError) || loadingSettings || saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </form>

        {loadingSettings && <p className="marketplace-muted">Loading current settings...</p>}
        {message.text && (
          <p className={message.isError ? 'marketplace-message-error' : 'marketplace-message-success'}>
            {message.text}
          </p>
        )}
      </div>
    </div>
  );
};

export default MarketplaceSettings;
